import React, { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { api, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CategoryOptions } from "@/components/TransactionModal";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, CircleAlert, ArrowRight } from "lucide-react";

export const CsvImportModal = ({ cats, trigger }) => {
  const qc = useQueryClient();
  const { activeUser } = useApp();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [reviewIdxs, setReviewIdxs] = useState([]);
  const [rememberMap, setRememberMap] = useState({});
  const fileRef = useRef();

  const reset = () => {
    setStep(1);
    setPreview(null);
    setRows([]);
    setReviewIdxs([]);
    setRememberMap({});
  };

  const previewMut = useMutation({
    mutationFn: (fd) => api.importPreview(fd),
    onSuccess: (data) => {
      setPreview(data);
      const mapped = data.rows.map((r, i) => ({ ...r, _idx: i }));
      setRows(mapped);
      setReviewIdxs(mapped.filter((r) => !r.category_id).map((r) => r._idx));
      setStep(2);
    },
    onError: () => toast.error("Could not parse CSV. Check the file format."),
  });

  const commitMut = useMutation({
    mutationFn: (payload) => api.importCommit(payload),
    onSuccess: (res) => {
      qc.invalidateQueries();
      toast.success(`Imported ${res.imported} transactions`, { description: res.rules_saved ? `${res.rules_saved} merchant rules saved for next time.` : undefined });
      setOpen(false);
      reset();
    },
  });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    previewMut.mutate(fd);
  };

  const setRowCat = (idx, catId) => {
    setRows((rs) => rs.map((r) => (r._idx === idx ? { ...r, category_id: catId } : r)));
  };

  const commit = () => {
    const new_rules = [];
    rows.forEach((r) => {
      if (rememberMap[r._idx] && r.category_id) {
        const keyword = (r.merchant || "").toLowerCase().replace(/[^a-z0-9 ]/g, "").split(" ").filter(Boolean).slice(0, 2).join(" ").trim();
        if (keyword) new_rules.push({ keyword, category_id: r.category_id });
      }
    });
    commitMut.mutate({
      rows: rows.map((r) => ({ amount: r.amount, merchant: r.merchant, date: r.date, category_id: r.category_id || null, user: activeUser })),
      new_rules,
    });
  };

  const reviewRows = rows.filter((r) => reviewIdxs.includes(r._idx));
  const autoRows = rows.filter((r) => !reviewIdxs.includes(r._idx));
  const stillUnassigned = rows.filter((r) => !r.category_id).length;
  const assignedCount = rows.length - stillUnassigned;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#1a2232] border-[#2a364f] text-slate-100 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#22c55e]" /> Smart CSV Import
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {step === 1 ? "Upload a bank export. Columns are auto-mapped and merchants auto-categorized." : "Review auto-categorized rows and assign anything unrecognized."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="py-6">
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="csv-dropzone"
              className="w-full border-2 border-dashed border-[#2a364f] rounded-2xl py-12 flex flex-col items-center gap-3 hover:border-[#22c55e] hover:bg-[#22c55e]/5 transition-colors"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#22c55e]/15 flex items-center justify-center">
                <Upload className="w-6 h-6 text-[#22c55e]" />
              </div>
              <div className="text-center">
                <p className="font-medium">{previewMut.isPending ? "Parsing…" : "Click to upload CSV"}</p>
                <p className="text-xs text-slate-500 mt-1">Date, Merchant/Description &amp; Amount columns</p>
              </div>
            </button>
            <input ref={fileRef} data-testid="csv-file-input" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
          </div>
        )}

        {step === 2 && preview && (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-[#0a0e14] border border-[#2a364f] p-3 text-center">
                <div className="text-2xl font-bold font-mono-num">{preview.total}</div>
                <div className="text-xs text-slate-500">rows</div>
              </div>
              <div className="rounded-xl bg-[#22c55e]/10 border border-[#22c55e]/30 p-3 text-center">
                <div className="text-2xl font-bold font-mono-num text-[#22c55e]">{assignedCount}</div>
                <div className="text-xs text-slate-400">categorized</div>
              </div>
              <div className="rounded-xl bg-[#a855f7]/10 border border-[#a855f7]/30 p-3 text-center">
                <div data-testid="unassigned-count" className="text-2xl font-bold font-mono-num text-[#a855f7]">{stillUnassigned}</div>
                <div className="text-xs text-slate-400">unassigned</div>
              </div>
            </div>

            <div className="text-xs text-slate-500">
              Mapped: <span className="text-slate-300">{preview.mapping.date}</span> → Date ·{" "}
              <span className="text-slate-300">{preview.mapping.merchant}</span> → Merchant ·{" "}
              <span className="text-slate-300">{preview.mapping.amount}</span> → Amount
            </div>

            {reviewRows.length > 0 && (
              <div data-testid="unassigned-bucket-card" className="rounded-xl bg-[#a855f7]/5 border border-[#a855f7]/20 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-[#a855f7]">
                  <CircleAlert className="w-4 h-4" /> Needs review · categorize &amp; optionally save a rule
                </div>
                {reviewRows.map((r) => (
                  <div key={r._idx} className="flex items-center gap-2 flex-wrap bg-[#0a0e14] rounded-lg p-2">
                    <div className="flex-1 min-w-[140px]">
                      <div className="text-sm">{r.merchant}</div>
                      <div className="text-xs text-slate-500 font-mono-num">{r.date} · {fmt(r.amount)}</div>
                    </div>
                    <Select value={r.category_id || undefined} onValueChange={(v) => setRowCat(r._idx, v)}>
                      <SelectTrigger data-testid={`assign-select-${r._idx}`} className="w-40 h-9 bg-[#1a2232] border-[#2a364f] text-xs">
                        <SelectValue placeholder="Categorize" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a2232] border-[#2a364f] text-slate-100 max-h-60">
                        <CategoryOptions cats={cats} />
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1.5">
                      <Checkbox data-testid="save-mapping-rule-checkbox" id={`rule-${r._idx}`} checked={!!rememberMap[r._idx]} onCheckedChange={(c) => setRememberMap((m) => ({ ...m, [r._idx]: c }))} className="border-[#2a364f] data-[state=checked]:bg-[#22c55e] data-[state=checked]:border-[#22c55e]" />
                      <label htmlFor={`rule-${r._idx}`} className="text-[11px] text-slate-400 cursor-pointer">Remember</label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {autoRows.length > 0 && (
              <div className="rounded-xl bg-[#22c55e]/5 border border-[#22c55e]/20 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-[#22c55e] mb-2">
                  <CheckCircle2 className="w-4 h-4" /> Auto-categorized ({autoRows.length})
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {autoRows.map((r) => (
                    <div key={r._idx} className="flex items-center justify-between text-xs py-1">
                      <span className="text-slate-300">{r.merchant}</span>
                      <span className="text-slate-500">
                        {cats.find((c) => c.id === r.category_id)?.name} · <span className="font-mono-num">{fmt(r.amount)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={reset} className="text-slate-400 hover:text-slate-200">Back</Button>
            <Button data-testid="confirm-import-button" onClick={commit} disabled={commitMut.isPending} className="bg-[#22c55e] hover:bg-[#16a34a] text-white gap-2">
              Import {rows.length} <ArrowRight className="w-4 h-4" />
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
